"use client";

import { type ChangeEvent, useEffect, useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { GanttDiagram } from "@/components/tool-kit/diagrams/gantt-diagram";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { estimateTextWidth } from "@/lib/tool-kit/charts/text";
import { diagramColor } from "@/lib/tool-kit/diagrams/color";
import { monthName, utcDate } from "@/lib/tool-kit/diagrams/date-format";
import { civilDate, dayNumber } from "@/lib/tool-kit/diagrams/dates";
import {
  type DependencyIssue,
  GANTT_MAX_YEARS,
  type GanttData,
  type GanttHeaderUnit,
  type GanttProblemKind,
  type GanttScale,
  ganttCsv,
  ganttRows,
  ganttTooLong,
  layoutGantt,
  parseGantt,
} from "@/lib/tool-kit/diagrams/gantt";
import { downloadText } from "@/lib/tool-kit/download";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const GANTT_CHART_MAKER_STRINGS = {
  dataLabel:
    "Tasks — one per line: task, start, end or duration, group, progress %, depends on",
  dataHint:
    "Dates are YYYY-MM-DD. The third column is an end date (the task includes it) or a duration: 10d is ten days, 3w three weeks, 0 a milestone. Group, progress and dependencies are optional; “depends on” takes task numbers (1 is the first task) or a task's name. Separate columns with commas, tabs or semicolons.",
  exampleData:
    "Task, Start, End or duration, Group, Progress %, Depends on\nLiterature review, 2027-01-11, 4w, Planning, 40,\nResearch proposal, 2027-02-08, 2w, Planning, , 1\nEthics approval, 2027-02-22, 3w, Planning, , 2\nProposal approved, 2027-03-15, 0, Planning, , 3\nData collection, 2027-03-15, 6w, Research, , 4\nData analysis, 2027-04-26, 4w, Research, , 5\nWrite results and discussion, 2027-05-24, 4w, Writing, , 6\nDraft to supervisor, 2027-06-21, 0, Writing, , 7\nRevisions, 2027-06-21, 2w, Writing, , 8\nSubmit thesis, 2027-07-05, 0, Writing, , 9",
  exampleTitle: "Master's Thesis Plan, January to July 2027",
  headTask: "Task",
  headStart: "Start",
  headEnd: "End or duration",
  headGroup: "Group",
  headProgress: "Progress %",
  headDepends: "Depends on",
  importCsv: "Import CSV",
  exportCsv: "Export CSV",
  importFailed: "That file could not be read as text.",
  scaleLabel: "Scale",
  scaleDay: "Days",
  scaleWeek: "Weeks",
  scaleMonth: "Months",
  weekends: "Shade weekends (day and week scales)",
  workingDays: "Count durations in working days, Monday to Friday",
  colorByGroup: "A color for each group",
  barColor: "Bar color (the first group)",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  today: "Today",
  tasksDetected: "one:{count} task|other:{count} tasks",
  days: "one:{count} day|other:{count} days",
  span: "{start} to {end}",
  problem: "Line {line} was left out: {reason}",
  noName: "it has no task name.",
  badStart: "the start date is not a YYYY-MM-DD date.",
  badEnd:
    "the third column is neither a YYYY-MM-DD end date nor a duration such as 5d or 2w.",
  endBeforeStart: "the end date is before the start date.",
  badProgress: "progress must be a number from 0 to 100.",
  unknownDependency:
    "Task {task} depends on “{reference}”, which is not a task number or name.",
  selfDependency: "Task {task} depends on itself, so that link was ignored.",
  earlyDependency:
    "“{task}” starts before “{on}” finishes, although it depends on it. The arrow is drawn dashed.",
  cycleDependency:
    "These tasks depend on each other in a circle, so none of them could start: {tasks}.",
  enterData: "Enter at least one task with a start date to draw the chart.",
  tooLong:
    "These tasks span more than {years} years, which is too long to draw — check the years in the dates.",
  durationColumn: "Duration",
  milestone: "Milestone",
  endColumn: "End",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Chart title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the tasks as a table",
  chartLabel: "Gantt chart",
  chartDescription: "Gantt chart of {tasks} from {start} to {end}.",
} as const;

type Strings = typeof GANTT_CHART_MAKER_STRINGS;

const PROBLEMS: Record<GanttProblemKind, keyof Strings> = {
  noName: "noName",
  badStart: "badStart",
  badEnd: "badEnd",
  endBeforeStart: "endBeforeStart",
  badProgress: "badProgress",
};

function longDate(locale: string, day: number): string {
  const { year, month, day: date } = civilDate(day);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(utcDate(year, month, date));
}

/** Header words that fit the room: "January 2027", "Jan 2027", "Jan", "J". */
function headerLabel(locale: string, fmt: NumberFormat) {
  return (unit: GanttHeaderUnit, day: number, room: number) => {
    const { year, month, day: date } = civilDate(day);
    if (unit === "day") {
      return fmt(date);
    }
    const yearText = fmt(year, { useGrouping: false });
    if (unit === "year") {
      return yearText;
    }
    const withYear = (width: "long" | "short") =>
      new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: width,
        timeZone: "UTC",
      }).format(utcDate(year, month, 1));
    const short = monthName(locale, month, "short");
    const narrow = monthName(locale, month, "narrow");
    const options =
      unit === "monthOnly"
        ? [monthName(locale, month, "long"), short, narrow]
        : [withYear("long"), withYear("short"), short, narrow];
    return (
      options.find(
        (text) => estimateTextWidth(text, { fontSize: 11, bold: true }) <= room
      ) ?? ""
    );
  };
}

function dependencyText(
  issue: DependencyIssue,
  data: GanttData,
  strings: Strings,
  fmt: NumberFormat
): string {
  const name = (number: number) => data.tasks[number - 1]?.name ?? fmt(number);
  if (issue.kind === "unknown") {
    return t(strings.unknownDependency, {
      task: fmt(issue.task),
      reference: issue.reference,
    });
  }
  if (issue.kind === "self") {
    return t(strings.selfDependency, { task: fmt(issue.task) });
  }
  if (issue.kind === "early") {
    return t(strings.earlyDependency, {
      task: name(issue.task),
      on: name(issue.on),
    });
  }
  return t(strings.cycleDependency, {
    tasks: issue.tasks.map(name).join(" → "),
  });
}

export default function GanttChartMakerWidget() {
  const strings = useToolUi(GANTT_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [scale, setScale] = useState<GanttScale>("week");
  const [weekends, setWeekends] = useState(true);
  const [workingDays, setWorkingDays] = useState(false);
  const [byGroup, setByGroup] = useState(true);
  const [colorId, setColorId] = useState<ChartColorId>("blue");
  const [today, setToday] = useState<number | null>(null);
  const [importError, setImportError] = useState(false);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  // Today is the reader's date, which the server cannot know.
  useEffect(() => {
    const now = new Date();
    setToday(dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate()));
  }, []);

  const data = parseGantt(raw, { workingDays });
  const tasks = data.tasks;
  const tooLong = ganttTooLong(tasks);
  const layout =
    tasks.length > 0 && !tooLong
      ? layoutGantt({
          tasks,
          scale,
          title: style.apa ? "" : style.title,
          weekends,
          headerLabel: headerLabel(locale, fmt),
        })
      : null;
  const start = colorIndex(colorId);
  const colors = (layout?.groups.length ? layout.groups : [""]).map(
    (_, index) => (byGroup ? diagramColor(start, index) : CHART_COLORS[start])
  );
  const csvHead = [
    strings.headTask,
    strings.headStart,
    strings.headEnd,
    strings.headGroup,
    strings.headProgress,
    strings.headDepends,
  ];
  const filename = chartFilename(style.title, "gantt-chart");
  const head = [
    strings.headTask,
    strings.headGroup,
    strings.headStart,
    strings.endColumn,
    strings.durationColumn,
    strings.headProgress,
    strings.headDepends,
  ];
  const rows = tasks.map((task) => [
    task.name,
    task.group,
    longDate(locale, task.start),
    task.milestone ? strings.milestone : longDate(locale, task.end - 1),
    task.milestone
      ? "0"
      : tPlural(strings.days, locale, task.end - task.start, {
          count: fmt(task.end - task.start),
        }),
    task.progress === null
      ? ""
      : fmt(task.progress / 100, { style: "percent" }),
    task.depends.map((number) => fmt(number)).join(", "),
  ]);
  const first = layout ? Math.min(...tasks.map((task) => task.start)) : 0;
  const last = layout
    ? Math.max(...tasks.map((task) => Math.max(task.end - 1, task.start)))
    : 0;

  async function importFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    try {
      setImportError(false);
      setRaw(await file.text());
    } catch {
      setImportError(true);
    }
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="gcm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={11}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <label
          className={`${BUTTON_SECONDARY} cursor-pointer focus-within:ring-2 focus-within:ring-primary`}
        >
          {strings.importCsv}
          <input
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            className="sr-only"
            id="gcm-import"
            onChange={importFile}
            type="file"
          />
        </label>
        <button
          className={BUTTON_SECONDARY}
          disabled={tasks.length === 0}
          onClick={() =>
            downloadText(
              `${filename}.csv`,
              ganttCsv(tasks, csvHead),
              "text/csv;charset=utf-8"
            )
          }
          type="button"
        >
          {strings.exportCsv}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {importError ? <ErrorNotice>{strings.importFailed}</ErrorNotice> : null}
        {layout ? (
          <p className={HINT}>
            {tPlural(strings.tasksDetected, locale, tasks.length, {
              count: fmt(tasks.length),
            })}
            {" · "}
            {t(strings.span, {
              start: longDate(locale, first),
              end: longDate(locale, last),
            })}
          </p>
        ) : null}
        {tasks.length === 0 ? <Notice>{strings.enterData}</Notice> : null}
        {tooLong ? (
          <Notice>{t(strings.tooLong, { years: fmt(GANTT_MAX_YEARS) })}</Notice>
        ) : null}
        {data.problems.map((problem) => (
          <Notice key={`${problem.line}-${problem.kind}`}>
            {t(strings.problem, {
              line: fmt(problem.line),
              reason: strings[PROBLEMS[problem.kind]],
            })}
          </Notice>
        ))}
        {data.dependencyIssues.map((issue) => {
          const text = dependencyText(issue, data, strings, fmt);
          return <Notice key={text}>{text}</Notice>;
        })}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.scaleLabel}
          name="gcm-scale"
          onChange={setScale}
          options={[
            { value: "day", label: strings.scaleDay },
            { value: "week", label: strings.scaleWeek },
            { value: "month", label: strings.scaleMonth },
          ]}
          value={scale}
        />
        <div className="space-y-2">
          <Checkbox
            checked={weekends}
            label={strings.weekends}
            onChange={setWeekends}
          />
          <Checkbox
            checked={workingDays}
            label={strings.workingDays}
            onChange={setWorkingDays}
          />
        </div>
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([csvHead, ...ganttRows(tasks)])}
          figureNumber={style.figureNumber}
          filename={filename}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <div className="overflow-x-auto">
            <GanttDiagram
              colors={colors}
              description={t(strings.chartDescription, {
                tasks: tPlural(strings.tasksDetected, locale, tasks.length, {
                  count: fmt(tasks.length),
                }),
                start: longDate(locale, first),
                end: longDate(locale, last),
              })}
              id="gcm-chart"
              label={style.title.trim() || strings.chartLabel}
              layout={layout}
              progressLabel={(value) => fmt(value / 100, { style: "percent" })}
              today={today}
              todayLabel={strings.today}
            />
          </div>
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="gcm"
        labels={strings}
        style={style}
        update={update}
      >
        <Checkbox
          checked={byGroup}
          label={strings.colorByGroup}
          onChange={setByGroup}
        />
        <ColorSwatches
          labels={strings}
          legend={strings.barColor}
          name="gcm-color"
          onChange={setColorId}
          value={colorId}
        />
      </ChartStyleFields>
    </div>
  );
}
